import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { deepCopy } from '../../_utilities/utils'
import { setLocalStorageItem } from '../../_data/storage'
import { DisplayUser } from '../../_types/user'
import { UserState, LocalUserCopy, UserStateUpdate } from './types'

const initialState: UserState = {
  id: '',
  firstName: '',
  lastName: '',
  displayName: '',
  email: '',
  companyId: '',
  companyCommonName: '',
  companyLegalName: '',
  role: 'unauthorized',
  isBusy: false,
}

/**
 * Save user data to local storage
 *
 * @param {UserState} state - Current user data
 * @returns {void}
 *
 * ```ts
 * saveUserLocally(state.user)
 * ```
 */
const saveUserLocally = (state: UserState) => {
  const copyOfUser: UserState = deepCopy(state)

  const saveableObject: LocalUserCopy = {
    id: copyOfUser.id,
    firstName: copyOfUser.firstName,
    lastName: copyOfUser.lastName,
    displayName: copyOfUser.displayName,
    email: copyOfUser.email,
    companyId: copyOfUser.companyId,
    companyCommonName: copyOfUser.companyCommonName,
    companyLegalName: copyOfUser.companyLegalName,
    role: copyOfUser.role,
    lastSave: Date.now(),
  }

  setLocalStorageItem('user', JSON.stringify(saveableObject))
}

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    saveUser: (state, action: PayloadAction<DisplayUser>) => {
      state.isBusy = true

      const localUser: UserState = {
        id: action.payload.id,
        firstName: action.payload.firstName,
        lastName: action.payload.lastName,
        displayName: action.payload.displayName,
        email: action.payload.email,
        companyId: action.payload.companyId,
        companyCommonName: action.payload.companyCommonName,
        companyLegalName: action.payload.companyLegalName,
        role: action.payload.role,
        isBusy: false,
      }

      Object.keys(state).forEach(key => {
        state[key] = localUser[key]
      })

      saveUserLocally(state)
      state.isBusy = false
    },
    getUser: state => deepCopy(state),
    reinitializeUser: (state, action: PayloadAction<LocalUserCopy>) => {
      state.isBusy = true

      state.id = action.payload.id
      state.firstName = action.payload.firstName
      state.lastName = action.payload.lastName
      state.displayName = action.payload.displayName
      state.email = action.payload.email
      state.role = action.payload.role
      state.companyId = action.payload.companyId

      saveUserLocally(state)
      state.isBusy = false
    },
    clearUser: state => {
      state.isBusy = true

      Object.keys(initialState).forEach(key => {
        state[key] = initialState[key]
      })

      saveUserLocally(state)
      state.isBusy = false
    },
    updateUser: (
      state,
      action: PayloadAction<UserStateUpdate | UserStateUpdate[]>,
    ) => {
      state.isBusy = true
      const payload: UserStateUpdate[] = Array.isArray(action.payload)
        ? action.payload
        : [action.payload]

      payload.forEach(updatedAttribute => {
        if (
          Object.prototype.hasOwnProperty.call(
            state,
            updatedAttribute.attribute,
          )
        ) {
          state[updatedAttribute.attribute] = updatedAttribute.value
        }
      })

      saveUserLocally(state)
      state.isBusy = false
    },
    setCompanyId: (state, action: PayloadAction<string>) => {
      state.isBusy = true

      state.companyId = action.payload
      saveUserLocally(state)

      state.isBusy = false
    },
  },
})

export { userSlice }

export default userSlice.reducer
